/**
 * User: ronald
 *
 * A mapping of facet information.
 * Exports an object with the following :
 * - map : a mapping of facet names to keys
 *      - "facetName" is the name of the facet that comes back from SOLR.
 *      - "facetKey" is the key you pass to SOLR in a filterQuery for faceting.
 *      - "filterKey" is a key used to filter a search.
 *      - "tagName" tagName is used as the value of a tag which is used by solr.  When doing a search, say on an id,
 *        the gui may need to show how many results there are for facets, but the ID search would restrict
 *        the result set to 1, leaving the facet count numbers to mostly just 0.  In order to exclude the
 *        restricting search from the facet results, you can pass a tag to prevent the search from limiting
 *        the result set.
 *        reference: https://wiki.apache.org/solr/SimpleFacetParameters#Tagging_and_excluding_Filters
 * - getNameFromKey : a function to get a facet name from a facet key
 * - getKeyFromName : a function to get a facet key from a facet name.
 * - getTagFromKey : a function to get a facet tag name from a facet key.
 *
 * If an entry in the map has a facetKey but no facetName, then it is not an available facet, but it can be used as a
 * SOLR filter query.
 * @type {object}
 */
"use strict";


// ---------------------------------------------------
// Dependencies

var _ = require('underscore');


//const FILTER_L1                 = 'vertical';
//const FILTER_L2                 = 'l2_url_label';
//const FILTER_L2_FINE_ART        = 'l2_url_label_fine_art';
//const FILTER_L3                 = 'l3_url_label';
//const FILTER_PERIOD             = 'period_url_label';
//const FILTER_CREATOR            = 'creator_id';
//const FILTER_CREATOR_LABEL      = 'creator_url_label';
//const FILTER_DEALER             = 'seller_url_label';
//const FILTER_LOCATION           = 'location_id';
//const FILTER_SHIPPING           = 'shipping_url_label';
//const FILTER_PRICE              = 'sort_price';
//const FILTER_CATEGORY_SEARCH    = 'category_search';
//const FILTER_STOREFRONT_SEARCH  = 'storefront_search';
//const FILTER_SECTION            = '%s_section_url_label';
//const FILTER_ASSOCIATION        = 'association_id';
//const FILTER_COLLECTION         = 'collection_id';
//const FILTER_SALE               = 'sale_id';
//const FILTER_SALE_LATEST        = 'sale_current_%d';
//const FILTER_GENDER             = 'gender_url_label';
//const FILTER_NEW_LISTINGS       = 'new_listing';
//const FILTER_STATUS             = 'status';
//const FILTER_HIDE_PRICE         = 'hideprice';
//const FILTER_WIDTH              = 'numeric_width';
//const FILTER_HEIGHT             = 'numeric_height';
//const FILTER_LENGTH             = 'numeric_height';
//const FILTER_PRIMARY_KEY        = 'pk';
//
//// Facets
//const FACET_L1          = 'vertical_facets';
//const FACET_L2          = 'l2_facets';
//const FACET_L2_FINE_ART = 'l2_facets_fine_art';
//const FACET_L3          = 'l3_facets';
//const FACET_PERIOD      = 'period_facets';
//const FACET_CREATOR     = 'creator_sort_facets';
//const FACET_DEALER      = 'seller_facets';
//const FACET_LOCATION    = 'location_facets';
//const FACET_SHIPPING    = 'shipping_facets';
//const FACET_SECTION     = '%s_section_facets';
//const FACET_ASSOCIATION = 'association_facets';
//const FACET_COLLECTION  = 'collection_facets';
//const FACET_SALE        = 'sale_facets';
//const FACET_GENDER      = 'gender_facets';
//
//// Stats
//const STATS_PRICE       = self::FILTER_PRICE;
//const STATS_WIDTH       = self::FILTER_WIDTH;
//const STATS_HEIGHT      = self::FILTER_HEIGHT;
//
//// Values
//const VAL_NO_SOLD       = '(-1 0 6)';
//const VAL_IGNORE_ZERO   = '[1 TO *]';


var map = {
    vertical : {
        facetName : 'vertical_facets',
        facetKey : 'vertical',
        tagName : 'v'
    },
    l2 : {
        facetName : 'l2_facets',
        facetKey : 'l2_url_label',
        tagName : 'l2'
    },
    l3 : {
        facetName : 'l3_facets',
        facetKey : 'l3_url_label',
        tagName : 'l3'
    },
    location : {
        facetName : 'location_facets',
        facetKey : 'location_id',
        tagName : 'loc'
    },
    period : {
        facetName : 'attr_opt_facet_period',
        facetKey : 'attr_opt_facet_period',
        filterKey: 'attr_opt_id_period',
        tagName: 'pr'
    },
    metal: {
        facetName: 'attr_opt_facet_metal',
        facetKey: 'attr_opt_facet_metal',
        filterKey: 'attr_opt_id_metal',
        tagName: 'met'
    },
    stone: {
        facetName: 'attr_opt_facet_stone',
        facetKey: 'attr_opt_facet_stone',
        filterKey: 'attr_opt_id_stone',
        tagName: 'st'
    },
    shipping : {
        facetName : 'shipping_facets',
        facetKey : 'shipping_url_label',
        tagName : 'shp'
    },
    fashionType: {
        facetName: 'ftype',
        facetKey: 'ftype',
        tagName: 'ftype'
    },
    creatorId : {
        facetName : 'creator_sort_facets',
        facetKey : 'creator_id',
        filterKey: 'creator',
        tagName : 'cr'
    },
    price : {
        facetKey : 'sort_price',
        tagName : 'pi'
    },
    status : {
        facetKey : 'status',
        tagName : 'st'
    },
    publishingOption: {
        facetKey: 'publishingOption',
        tagName: 'publishingOption'
    },
    itemReference : {
        facetKey : 'reference',
        tagName : 're'
    },
    // Used in OMT - via transaction endpoint
    sellerId : {
        facetKey : 'sellerId',
        tagName : 'sid'
    },
    // Used in inventory search against the item endpoint
    sellerPk : {
        facetKey : 'seller_pk',
        tagName : 'spk'
    },
    buyerId : {
        facetKey : 'buyerId',
        tagName : 'bid'
    },
    itemId : {
        facetKey : 'itemId',
        tagName : 'iid'
    },
    itemPk : {
        facetKey : 'pk',
        tagName : 'ipk'
    },
    searchQuery : {
        facetKey : 'query',
        tagName : 'query'
    },
    dealerCompany : {
        facetKey : 'seller_name',
        tagName : 'seller_name'
    },
    dealerReference : {
        facetKey : 'dealerreference',
        tagName : 'dealerreference'
    },
    dealerEmail : {
        facetKey : 'seller_email',
        tagName: 'seller_email'
    },
    onHold: {
        facetKey: 'on_hold',
        tagName: 'on_hold'
    },
    csStatus: {
        facetKey: 'csstatus',
        tagName: 'csstatus'
    },
    association: {
        facetKey: 'association_url_label',
        tagName: 'association'
    },
    collection: {
        facetKey: 'collection_url_label',
        tagName: 'collection'
    }
};

// Map all of the facet names to keys, and vice versa :
var _nameMap = {};
var _keyMap = {};
var _tagMap = {};
var _filterKeyTagMap = {};

_.each(map, function (val) {
    if (val.facetName && val.facetKey) {
        _nameMap[val.facetName] = val.facetKey;
    }
});

_.each(map, function (val) {
    if (val.facetName && val.facetKey) {
        _keyMap[val.facetKey] = val.facetName;
    }
});

_.each(map, function (val) {
    if (val.tagName && val.facetKey) {
        _tagMap[val.facetKey] = val.tagName;
        if (val.filterKey) {
            _filterKeyTagMap[val.filterKey] = val.tagName;
        }
    }
});

var _getNameFromKey = function (key) {
    return _keyMap[key];
};
var _getKeyFromName = function (name) {
    return _nameMap[name];
};
var _getTagFromKey = function (key) {
    return _tagMap[key] || _filterKeyTagMap[key];
};

var _getAssociationValue = function (key) {
    var associationValues = {
        nydc: 'itemStatus.NYDCItems',
        aadla: 'itemStatus.approvedAntiquesAssociations',
        twentiethLeague: 'itemStatus.20thCenturySpecialists',
        adaa: 'itemStatus.approvedArtDealersAssociations',
        aipad: 'itemStatus.approvedAIPAD'
    };

    return associationValues[key];
};

module.exports = {
    facetMap: map,
    getNameFromKey : _getNameFromKey,
    getKeyFromName : _getKeyFromName,
    getTagFromKey : _getTagFromKey,
    getAssociationValue: _getAssociationValue
};



//////////////////
// WEBPACK FOOTER
// ./~/bunsen/helpers/SolrHelper.js
// module id = 2629
// module chunks = 63